import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, model, OnInit, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef, MatDialogTitle, } from '@angular/material/dialog';
import { LocationAllocation, UserManagementService, UserRequestVo } from '../user-management.service';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { SimpleCodeValue } from '../../../core/models/code-value.model';
import { FormsModule } from '@angular/forms';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SelectionModel } from '@angular/cdk/collections';
import { AuthContext } from '../../../auth/services/auth.context';
import { LookupItem } from '../../../core/services/lookup.service';

export interface DialogData {
  userRequest: any;
  event: UserRequestVo;
  complaintId: number;
  message: string;
  title: string;
  type: string;
  areas: SimpleCodeValue[];
}

@Component({
  selector: 'app-user-request-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatSelectModule,
    MatInputModule,
    MatFormFieldModule,
    FormsModule,
    MatTableModule,
    MatCheckboxModule,
    MatPaginatorModule,
    MatSortModule,
  ],
  templateUrl: './user-request-dialog.component.html',
  styleUrl: './user-request-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserRquestPopupDialogComponent implements OnInit, AfterViewInit {

  readonly dialogRef = inject(MatDialogRef<UserRquestPopupDialogComponent>);
  readonly data = inject<DialogData>(MAT_DIALOG_DATA);
  readonly event = model(this.data.event); // Only valid at class member level
  readonly title = model(this.data.title);
  readonly message = model(this.data.message);
  readonly type = model(this.data.type);

  areaId!: number;
  disableApproveButton: boolean = true;
  dataSource = new MatTableDataSource<LocationAllocation>([]);
  selection = new SelectionModel<LocationAllocation>(false, []);
  displayedColumns: string[] = ['locationCode', 'locationName', 'quarterNumber', 'blockNumber', 'residentName', 'select'];
  locationSelected: boolean = false;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  locationTypes: LookupItem[] = [];

  constructor(
    private userManagementService: UserManagementService,
    private cdr: ChangeDetectorRef,
    private authContext: AuthContext
  ) { }

  ngOnInit(): void {
    const lookups = this.authContext.getLookups();
    this.locationTypes = lookups['LOCATION_TYPE'] || [];
  }

  ngAfterViewInit() {
    // Set paginator and sort to dataSource after view initialization
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.cdr.detectChanges();
  }

  onNoClick(): void {
    this.dialogRef.close({ approved: false, location: null });
  }

  onApproved() {
    this.dialogRef.close({ approved: true, location: this.selection.selected[0] });
  }

  getLocationType(lookupCode: string) {
    const locationType = this.locationTypes.find(item => item.lookupCode === lookupCode);
    return locationType?.lookupValue ? locationType?.lookupValue : lookupCode;
  }

  onAreaSelect() {
    this.userManagementService.getUserAllocationsByAreaId(this.areaId).subscribe({
      next: (response) => {
        // console.log(response);
        if (response) {
          this.dataSource = new MatTableDataSource(response);
          this.dataSource.paginator = this.paginator;
          this.dataSource.sort = this.sort;
          this.disableApproveButton = false;
          this.cdr.detectChanges();
        }
      },
      error: (error) => {
        console.error('POST error:', error);
        this.onNoClick();
      },
    });
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
      this.dataSource.paginator = this.paginator;
      this.dataSource.sort = this.sort;
    }
  }

  onCheckboxChange(location: LocationAllocation) {
    if (this.selection.isSelected(location)) {
      this.selection.deselect(location);
    } else {
      this.selection.select(location);
    }
    this.locationSelected = this.selection.selected.length > 0;
  }
}
